import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { findById, deleteById, update } from "@/apis/template";
import { TemplateDto } from "@/dto/TemplatesDto";
import { useNavigate } from "react-router-dom";
import Router from "@/constants/router";
import { useModal } from "mui-modal-provider";
import { Confirm } from "@/organism/Confirm";
import { enqueueSnackbar } from "notistack";

export default function useDetail() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [templateInfo, setTemplateInfo] = useState<TemplateDto | null>(null);
  const navigate = useNavigate();
  const { showModal } = useModal();

  /**
   * 템플릿 상세 정보 조회
   */
  const fetchTemplateData = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        const result = await findById(id);
        if (result.success) {
          setTemplateInfo(result.data);
        }
      } catch (e) {
        console.error(e);
        navigate(Router.Template);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (id) {
      fetchTemplateData(id);
    }
  }, [id, fetchTemplateData]);

  /**
   * 템플릿 삭제
   */
  const handleTemplateDelete = async (id: string) => {
    if (!id) return;

    const modal = showModal(Confirm, {
      title: "템플릿 삭제",
      content:
        "정말로 이 템플릿을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.",
      okTitle: "삭제",
      cancelTitle: "취소",
      onOk: async () => {
        modal.destroy();
        try {
          setIsLoading(true);
          const result = await deleteById(id);

          if (result.success) {
            enqueueSnackbar("템플릿이 성공적으로 삭제되었습니다.", {
              variant: "success",
            });
            navigate(Router.Template);
          } else {
            enqueueSnackbar(
              result.message || "템플릿 삭제 중 오류가 발생했습니다.",
              {
                variant: "error",
              }
            );
          }
        } catch (e) {
          console.error(e);
          enqueueSnackbar("템플릿 삭제 중 오류가 발생했습니다.", {
            variant: "error",
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel: () => {
        modal.destroy();
      },
    });
  };

  /**
   * 템플릿 수정
   */
  const handleTemplateUpdate = async (data: FormData, templateId: string) => {
    const modal = showModal(Confirm, {
      title: "템플릿 수정",
      content: "템플릿 정보를 수정하시겠습니까?",
      okTitle: "수정",
      cancelTitle: "취소",
      onOk: async () => {
        modal.destroy();
        try {
          setIsLoading(true);
          const result = await update(templateId, data);

          if (result.success) {
            enqueueSnackbar("템플릿이 성공적으로 수정되었습니다.", {
              variant: "success",
            });
            // 수정 후 최신 정보로 다시 조회
            fetchTemplateData(templateId);
          } else {
            enqueueSnackbar(
              result.message || "템플릿 수정 중 오류가 발생했습니다.",
              {
                variant: "error",
              }
            );
          }
        } catch (e) {
          console.error(e);
          enqueueSnackbar("템플릿 수정 중 오류가 발생했습니다.", {
            variant: "error",
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel: () => {
        modal.destroy();
      },
    });
  };

  return {
    isLoading,
    templateInfo,
    handleTemplateDelete,
    handleTemplateUpdate,
  };
}
